import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogIn, MessageCircle, ClipboardList } from 'lucide-react';
import logoAsset from '@/assets/balham-babes-logo.svg.asset.json';
import { optimizeImage } from '@/lib/imageUrl';
import { isMedianApp } from '@/lib/medianOneSignal';

import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useUserProfile } from '@/hooks/useUserProfile';
import { NotificationBell } from '@/components/NotificationBell';
import { FloatingAddButton } from '@/components/FloatingAddButton';
import { MoreMenuSheet } from '@/components/MoreMenuSheet';
import { useConversations } from '@/hooks/useConversations';
import { usePlaydatesBadge } from '@/hooks/usePlaydatesBadge';
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { UserProfile } from '@/components/auth/UserProfile';

export const Navigation = () => {
  const { user, loading } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile();
  const { isAdmin } = useIsAdmin();
  const [showProfile, setShowProfile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalUnread: messageUnread } = useConversations({ enabled: location.pathname !== '/admin' });
  const playdatesNewCount = usePlaydatesBadge();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleProtectedNavigation = (path: string, title: string, message: string) => {
    if (!user) {
      navigate(`/auth?returnUrl=${encodeURIComponent(path)}&title=${encodeURIComponent(title)}&message=${encodeURIComponent(message)}`);
    } else {
      navigate(path);
    }
  };

  return (
    <>
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-50 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              {!loading && <div className="-ml-2"><MoreMenuSheet /></div>}
              <Link to={isMedianApp() ? '/activities' : '/'} className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity no-underline">
                <img src={logoAsset.url} alt="Balham Babes" className="h-7 sm:h-8 w-auto" />
              </Link>
            </div>

            {/* Center Navigation */}
            <nav className="hidden md:flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className={`text-sm font-medium transition-all ${
                  isActive('/activities')
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => navigate('/activities')}
              >
                Activities & Cafés
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`text-sm font-medium transition-all ${
                  isActive('/community')
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => navigate('/community')}
              >
                Community
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`relative text-sm font-medium transition-all ${
                  isActive('/playdates')
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/playdates');
                }}
              >
                Playdates
                {playdatesNewCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 min-w-[16px] flex items-center justify-center p-0 px-1 bg-bright-fuschia text-white text-[9px] border-0">
                    {playdatesNewCount > 9 ? '9+' : playdatesNewCount}
                  </Badge>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`text-sm font-medium transition-all ${
                  isActive('/noticeboard')
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => navigate('/noticeboard')}
              >
                Noticeboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`text-sm font-medium transition-all ${
                  isActive('/stories')
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => navigate('/stories')}
              >
                Local Spotlights
              </Button>

              {/* Meetups nav hidden for now */}
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              ) : user ? (
                <>
                  <FloatingAddButton variant="nav" />
                  <NotificationBell />
                  <button

                    onClick={() => navigate('/messages')}
                    className={`flex items-center justify-center relative p-2 rounded-md transition-colors ${
                      isActive('/messages') || location.pathname.startsWith('/messages/')
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                    aria-label="Messages"
                  >
                    <MessageCircle className="h-5 w-5" />
                    {messageUnread > 0 && (
                      <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center p-0 px-1 bg-primary text-primary-foreground text-[9px] border-0">
                        {messageUnread > 9 ? '9+' : messageUnread}
                      </Badge>
                    )}
                  </button>
                  {/* Mobile Profile Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowProfile(true)}
                    className="flex md:hidden items-center hover:bg-muted/50 p-1 rounded-lg"
                  >
                    <Avatar className="h-9 w-9 rounded-lg">
                      <AvatarImage
                        src={optimizeImage(profile?.avatar_url, { width: 64, height: 64, resize: 'cover' }) || undefined}
                        alt={profile?.first_name || 'User'}
                        loading="lazy"
                        fetchPriority="low"
                        decoding="async"
                        className="rounded-lg"
                      />


                      <AvatarFallback className="text-sm bg-primary text-primary-foreground rounded-lg">
                        {profile?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                  {/* Mobile Profile Sheet */}
                  <Sheet open={showProfile} onOpenChange={setShowProfile}>
                    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto pt-12">
                      <SheetHeader className="pr-8">
                        <SheetTitle>Profile</SheetTitle>
                      </SheetHeader>
                      <div className="mt-4 pb-20">
                        <UserProfile userId={user.id} onNavigate={() => setShowProfile(false)} />
                      </div>
                    </SheetContent>
                  </Sheet>
                  {/* Desktop Profile Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowProfile(!showProfile)}
                    className="hidden md:flex items-center space-x-2 hover:bg-muted/50 p-1 rounded-lg"
                  >
                    <Avatar className="h-9 w-9 rounded-lg">
                    <AvatarImage
                        src={optimizeImage(profile?.avatar_url, { width: 72, height: 72, resize: 'cover' }) || undefined}
                        alt={profile?.first_name || 'User'}
                        loading="lazy"
                        fetchPriority="low"
                        decoding="async"
                        className="rounded-lg"
                      />


                      <AvatarFallback className="text-sm bg-primary text-primary-foreground rounded-lg">
                        {profile?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>

                    {isAdmin && (
                      <Badge className="bg-blue-500 text-white border-0 text-[9px] px-1 py-0 font-semibold uppercase leading-tight">
                        Admin
                      </Badge>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <FloatingAddButton variant="nav" />
                  {/* Mobile Sign In */}

                  <Link to="/auth" className="block md:hidden no-underline">
                    <Button variant="default" size="sm" className="flex items-center gap-1 px-3">
                      <LogIn className="h-4 w-4" />
                      <span className="text-xs">Sign in</span>
                    </Button>
                  </Link>
                  {/* Desktop Sign In */}
                  <Link to="/auth" className="hidden md:block no-underline">
                    <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                      <LogIn className="h-4 w-4" />
                      <span>Sign in / Sign up</span>
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>


      {/* Desktop User Profile Dropdown */}
      {showProfile && user && (
        <div className="relative hidden md:block">
          <div className="absolute top-0 right-4 z-50 w-80">
            <Card className="border-0 shadow-soft bg-card">
              <CardContent className="p-4">
                <UserProfile userId={user.id} onNavigate={() => setShowProfile(false)} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowProfile(false)}
                  className="mt-2 w-full"
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};
